import type { Mission } from "@rogoag/shared-ts";
import type { Job } from "@domain/CRM/job";
import type { CoreUpdateInput, FudgeCoreInput } from "@domain/MissionControl";
import type { IMissionLoader } from "@ports/missions/IMissionLoader";
import type { IMissions } from "@ports/missions/IMissions";

export interface MissionsUseCasesDeps {
	missionLoader: IMissionLoader;
	missionsApi: IMissions;
}

/**
 * Bound mission use cases consumed by the GUI/CLI. The loader half delegates
 * to the injected `IMissionLoader` (whose adapter owns the per-Job caches);
 * the mutate half calls `IMissions`, whose endpoints re-run the job's exit
 * interview themselves before answering, so the job's "issues" status already
 * reflects the new core coordinates when the call resolves. Each method is independently testable
 * by importing the underlying function and passing a stub deps object — the
 * factory exists to bundle the deps once at composition time so consumers
 * see a single namespace.
 */
export interface MissionsUseCases {
	loadExe(job: Job): Promise<Mission | undefined>;
	loadInt(job: Job): Promise<Mission | undefined>;
	loadPtsShp(job: Job, expectedType?: string): Promise<Mission | "no-attachment" | "download-failed" | "parse-failed">;
	fudgeCores(jobId: string, inputs: FudgeCoreInput[]): Promise<void>;
	updateCores(jobId: string, inputs: CoreUpdateInput[]): Promise<void>;
	deleteCores(jobId: string, coreIds: string[]): Promise<void>;
	setAverageCoreLength(jobId: string, coreIds: string[]): Promise<void>;
}

/**
 * Submits a fudge-core payload to the Missions backend, which re-runs the
 * exe-mission-validation pipeline. No-ops cleanly when `inputs` is empty so
 * callers don't need their own empty-list guard. Resolves once the re-run has
 * finished; the caller owns the cache invalidation + reselect that follow.
 */
async function fudgeCores(deps: MissionsUseCasesDeps, jobId: string, inputs: FudgeCoreInput[]): Promise<void> {
	if (inputs.length === 0) return;
	await deps.missionsApi.fudgeCores(jobId, inputs);
}

/**
 * Submits a manual core-edit payload (pulled lat/lon + length) to the Missions
 * backend, which re-runs the exe-mission-validation pipeline. No-ops cleanly when
 * `inputs` is empty. Resolves once the re-run has finished; the caller owns the
 * cache invalidation + reselect.
 */
async function updateCores(deps: MissionsUseCasesDeps, jobId: string, inputs: CoreUpdateInput[]): Promise<void> {
	if (inputs.length === 0) return;
	await deps.missionsApi.updateCores(jobId, inputs);
}

/** Deletes a list of cores by id; the backend then re-runs exe-mission-validation. */
async function deleteCores(deps: MissionsUseCasesDeps, jobId: string, coreIds: string[]): Promise<void> {
	await deps.missionsApi.deleteCores(jobId, coreIds);
}

/** Sets the supplied cores' length to the average of all cores for that
 *  job; the backend then re-runs exe-mission-validation. */
async function setAverageCoreLength(deps: MissionsUseCasesDeps, jobId: string, coreIds: string[]): Promise<void> {
	await deps.missionsApi.setAverageCoreLength(jobId, coreIds);
}

export function makeMissionsUseCases(deps: MissionsUseCasesDeps): MissionsUseCases {
	return {
		loadExe: (job) => deps.missionLoader.loadExe(job),
		loadInt: (job) => deps.missionLoader.loadInt(job),
		loadPtsShp: (job, expectedType) => deps.missionLoader.loadPtsShp(job, expectedType),
		fudgeCores: (jobId, inputs) => fudgeCores(deps, jobId, inputs),
		updateCores: (jobId, inputs) => updateCores(deps, jobId, inputs),
		deleteCores: (jobId, coreIds) => deleteCores(deps, jobId, coreIds),
		setAverageCoreLength: (jobId, coreIds) => setAverageCoreLength(deps, jobId, coreIds),
	};
}
